import React, { useRef, useState } from "react";
import useApi from "@/hooks/useApi";

interface Message {
  role: "user" | "assistant";
  content: string;
}

interface Props {
  reportName: string;
  reportData: Record<string, unknown>;
  starters?: string[];
}

const DEFAULT_STARTERS: Record<string, string[]> = {
  "Value Report": [
    "What's driving the deflection rate trend?",
    "Where should we focus to improve ROI?",
    "How does our response time compare to benchmarks?",
  ],
  "Agent Performance": [
    "Who are our top performers this period?",
    "Which agents need coaching?",
    "What does CSAT trend tell us?",
  ],
  "CSAT Results": [
    "What's causing low scores?",
    "Which channels have best satisfaction?",
    "How has CSAT trended over time?",
  ],
  "Chat Intelligence": [
    "What topics are customers asking most about?",
    "Are there gaps the bot isn't handling?",
    "Which intents have the worst resolution rate?",
  ],
  "Leaderboard": [
    "What separates top agents from the rest?",
    "Who improved the most this period?",
  ],
};

export default function MoshkyAnalyticsPanel({ reportName, reportData, starters }: Props) {
  const api = useApi();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const bottomRef = useRef<HTMLDivElement>(null);

  const chips = starters ?? DEFAULT_STARTERS[reportName] ?? [];

  const context = `You are Moshky, an analytics expert analyzing Velaro's ${reportName}.
Here is the current report data:
${JSON.stringify(reportData, null, 2)}

Be concise and specific — reference actual numbers from the data above. If asked for recommendations, ground them in the data. Don't hedge excessively.`;

  async function send(text: string) {
    if (!text.trim() || loading) return;
    setError(null);
    const userMsg: Message = { role: "user", content: text };
    const next = [...messages, userMsg];
    setMessages(next);
    setInput("");
    setLoading(true);
    setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: "smooth" }), 50);

    try {
      const res = await fetch(`${import.meta.env.VITE_ADMIN_API_URL}/Copilot/Chat`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(api.admin as any).getCommonHeaders(),
        },
        body: JSON.stringify({ messages: next, context }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error ?? `HTTP ${res.status}`);
      setMessages(prev => [...prev, { role: "assistant", content: data.reply }]);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
      setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: "smooth" }), 50);
    }
  }

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        title="Ask Moshky about this report"
        style={{
          display: "flex", alignItems: "center", gap: 8,
          background: "var(--color-primary, #6366f1)", color: "#fff",
          border: "none", borderRadius: 10, padding: "9px 16px",
          fontSize: 13.5, fontWeight: 600, cursor: "pointer",
          boxShadow: "0 2px 8px rgba(99,102,241,.35)",
        }}
      >
        <span style={{ fontSize: 16 }}>🤖</span> Ask Moshky
      </button>
    );
  }

  return (
    <div style={{
      background: "var(--bg-primary, #fff)", border: "1px solid var(--border-primary, #e5e7eb)",
      borderRadius: 14, overflow: "hidden",
      boxShadow: "0 4px 24px rgba(0,0,0,.08)",
      display: "flex", flexDirection: "column", height: 480,
    }}>
      {/* Header */}
      <div style={{
        display: "flex", alignItems: "center", gap: 10,
        padding: "12px 16px", borderBottom: "1px solid var(--border-primary, #e5e7eb)",
        background: "var(--bg-secondary, #f9fafb)",
      }}>
        <span style={{ fontSize: 20 }}>🤖</span>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 700, fontSize: 14 }}>Moshky</div>
          <div style={{ fontSize: 11.5, color: "var(--text-secondary, #6b7280)" }}>Analyzing {reportName}</div>
        </div>
        <button
          onClick={() => setOpen(false)}
          style={{ background: "none", border: "none", cursor: "pointer", fontSize: 18, color: "var(--text-secondary, #9ca3af)", lineHeight: 1 }}
        >×</button>
      </div>

      {/* Messages */}
      <div style={{ flex: 1, overflowY: "auto", padding: "14px 16px", display: "flex", flexDirection: "column", gap: 10 }}>
        {messages.length === 0 && (
          <div style={{ color: "var(--text-secondary, #6b7280)", fontSize: 13, lineHeight: 1.6 }}>
            I've loaded the {reportName} data. Ask me anything — I can identify trends, flag outliers, or suggest where to focus.
          </div>
        )}
        {messages.map((m, i) => (
          <div key={i} style={{
            maxWidth: "88%", padding: "9px 13px", borderRadius: 12, fontSize: 13.5, lineHeight: 1.55, whiteSpace: "pre-wrap", wordBreak: "break-word",
            alignSelf: m.role === "user" ? "flex-end" : "flex-start",
            background: m.role === "user" ? "var(--color-primary, #6366f1)" : "var(--bg-secondary, #f3f4f6)",
            color: m.role === "user" ? "#fff" : "var(--text-primary, #111827)",
            borderBottomRightRadius: m.role === "user" ? 3 : 12,
            borderBottomLeftRadius: m.role === "assistant" ? 3 : 12,
            border: m.role === "assistant" ? "1px solid var(--border-primary, #e5e7eb)" : "none",
          }}>
            {m.content}
          </div>
        ))}
        {loading && (
          <div style={{ alignSelf: "flex-start", color: "var(--text-secondary, #9ca3af)", fontSize: 12, padding: "4px 8px" }}>
            Moshky is thinking…
          </div>
        )}
        {error && (
          <div style={{ alignSelf: "flex-start", color: "#dc2626", fontSize: 12.5, padding: "6px 10px", background: "#fef2f2", borderRadius: 8 }}>
            ⚠️ {error}
          </div>
        )}
        <div ref={bottomRef} />
      </div>

      {/* Quick chips */}
      {chips.length > 0 && messages.length < 2 && (
        <div style={{ padding: "0 14px 10px", display: "flex", flexWrap: "wrap", gap: 6 }}>
          {chips.map(c => (
            <button key={c} onClick={() => send(c)} style={{
              fontSize: 12, padding: "4px 10px", border: "1px solid var(--border-primary, #d1d5db)",
              borderRadius: 999, background: "var(--bg-secondary, #f9fafb)", cursor: "pointer",
              color: "var(--text-secondary, #374151)", transition: "all .12s",
            }}>
              {c}
            </button>
          ))}
        </div>
      )}

      {/* Input */}
      <div style={{ padding: "10px 14px 14px", borderTop: "1px solid var(--border-primary, #e5e7eb)", display: "flex", gap: 8 }}>
        <input
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(input); } }}
          placeholder="Ask about this report…"
          disabled={loading}
          style={{
            flex: 1, border: "1px solid var(--border-primary, #d1d5db)", borderRadius: 8,
            padding: "8px 12px", fontSize: 13.5, background: "var(--bg-primary, #fff)",
            color: "var(--text-primary, #111827)", outline: "none",
          }}
        />
        <button
          onClick={() => send(input)}
          disabled={loading || !input.trim()}
          style={{
            background: "var(--color-primary, #6366f1)", color: "#fff", border: "none",
            borderRadius: 8, padding: "8px 16px", fontSize: 13.5, fontWeight: 600,
            cursor: loading || !input.trim() ? "not-allowed" : "pointer",
            opacity: loading || !input.trim() ? 0.6 : 1,
          }}
        >Send</button>
      </div>
    </div>
  );
}
